import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Table } from "react-bootstrap";

import { vacancyQuery, vacationBalancesGetAll } from "../../api";
import { LoadingContainer } from "../../common/loaders/loadingcontainer";
import { useSelector } from "react-redux";
import { activeIntegrationsSelector } from "../../reducers";

export function VacationDaysTable({ cardProps, changeProperty }) {
  const { t } = useTranslation();

  const dataSource = cardProps?.dataSource ?? "payroll"; // payroll | hr
  const selectedYear = cardProps?.year;

  const { data: payrollData } = useQuery({ ...vacancyQuery });

  const { data: hrData } = useQuery({
    queryKey: ["hrVacancy"],
    queryFn: vacationBalancesGetAll,
  });

  const [rows, setRows] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);

  const activeIntegrations = useSelector(activeIntegrationsSelector);
  const isProcountorActive = !!activeIntegrations.tokenProcountor;

  const safeNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const isEmptyRow = (r) => {
    const used = safeNumber(r.used);
    const remaining = safeNumber(r.remaining);
    return used === 0 && remaining === 0;
  };

  useEffect(() => {
    if (dataSource !== "payroll") return;
    if (!payrollData?.vacancy_response?.lomarp) return;

    const cleaned = payrollData.vacancy_response.lomarp
      .map(i => ({
        personName: i.nimi,
        used: safeNumber(i.pid),
        remaining: safeNumber(i.jalj),
        year: String(i.vuosi),
      }))
      .filter(i => i.personName);

    setRows(cleaned);

    const years = [...new Set(cleaned.map(x => x.year))].sort().reverse();
    setAvailableYears(years);

    if (!selectedYear && years.length > 0) {
      changeProperty("year", years[0]);
    }
  }, [payrollData, dataSource]);

  useEffect(() => {
    if (dataSource !== "hr") return;
    if (!Array.isArray(hrData?.data)) return;

    const mapped = hrData.data.map(p => {
      const allowance = safeNumber(p.vacationAllowanceDays);
      const used = safeNumber(p.usedVacationDays);

      return {
        personName: p.name,
        used,
        remaining: Math.max(0, allowance - used),
        year: p.annualVacationYear,
      };
    });

    setRows(mapped);

    const years = [...new Set(mapped.map(x => x.year))].sort().reverse();
    setAvailableYears(years);

    if (!selectedYear && years.length > 0) {
      changeProperty("year", years[0]);
    }
  }, [hrData, dataSource]);

  const filteredRows = useMemo(() => {
    if (!selectedYear) return [];

    return rows
      .filter(r => r.year === selectedYear)
      .filter(r => !isEmptyRow(r));
  }, [rows, selectedYear]);

  if (!filteredRows) return <LoadingContainer />;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h4 style={{ color: "var(--heeros-blue)" }}>
          {t("VacationDays", "Lomapäivät")}
        </h4>

        <select
          style={{ fontSize: "small" }}
          value={dataSource}
          onChange={(e) => {
            changeProperty("dataSource", e.target.value);
            changeProperty("year", undefined);
          }}
        >
          <option value="payroll">
            {t("InPayroll", "Palkanlaskennassa")}
          </option>
          <option value="hr">
            {t("InHRSystem", "HR-ohjelmassa")}
          </option>
        </select>

        {availableYears?.length > 0 && (
          <select
            style={{ fontSize: "small" }}
            value={selectedYear || ""}
            onChange={(e) => changeProperty("year", e.target.value)}
          >
            {availableYears.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        )}
      </div>

      <div style={{ overflowX: "auto", marginTop: 10 }}>
        <Table bordered hover size="sm">
          <thead>
            <tr>
              <th>Henkilö</th>
              <th style={{ background: "#f2f2f2" }}>
                Käytetty
              </th>
              <th style={{ background: "#f2f2f2" }}>
                Jäljellä
              </th>
            </tr>
          </thead>

          <tbody>
            {filteredRows.map((r, i) => (
              <tr key={i}>
                <td>{r.personName}</td>
                <td style={{ background: "#f2f2f2" }}>{r.used}</td>
                <td style={{ background: "#f2f2f2" }}>{r.remaining}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </>
  );
}